import { mapValues, sortBy } from "lodash";

import { IAvailableFilter } from "components/filters";
import { agentAssignmentFilterConfig } from "components/filters/agent-assignment-filter";
import { agentFilterConfigMultiple } from "components/filters/agent-filter";
import { attachmentsFilterConfig } from "components/filters/attachments-filter";
import { creationDateRangeFilterConfig } from "components/filters/creation-date-range-filter";
import { followersFilterConfig } from "components/filters/followers-filter";
import { groupFilterConfigMultiple } from "components/filters/group-filter";
import { languageFilterConfig } from "components/filters/language-filter/default-config";
import { lastActivityDateRangeFilterConfig } from "components/filters/last-activity-date-range-filter";
import { lastMessageDateRangeFilterConfig } from "components/filters/last-message-date-range-filter";
import { priorityFilterConfig } from "components/filters/priority-filter";
import { ratingDateRangeFilterConfig } from "components/filters/rating-date-range-filter";
import { ratingFilterConfig } from "components/filters/rating-filter";
import { resolutionDateRangeFilterConfig } from "components/filters/resolution-date-range-filter";
import { responseAuthorFilterConfig } from "components/filters/response-author-filter";
import { responseDateRangeFilterConfig } from "components/filters/response-date-range-filter";
import { tagFilterConfigMultiple } from "components/filters/tag-filter";
import { ticketSourceFilterConfig } from "components/filters/ticket-source-filter";
import { ticketStatusFilterConfig } from "components/filters/ticket-status-filter";
import { Filter } from "constants/filters";

import { TicketsRoutes } from "./TicketsRoutes";

export const ticketFilters: { [key: string]: IAvailableFilter } = {
  [Filter.Agent]: { filter: Filter.Agent, config: agentFilterConfigMultiple },
  [Filter.AgentAssignment]: { filter: Filter.AgentAssignment, config: agentAssignmentFilterConfig },
  [Filter.Attachments]: { filter: Filter.Attachments, config: attachmentsFilterConfig },
  [Filter.CreationDateRange]: { filter: Filter.CreationDateRange, config: creationDateRangeFilterConfig },
  [Filter.Language]: { filter: Filter.Language, config: languageFilterConfig },
  [Filter.LastActivityDateRange]: { filter: Filter.LastActivityDateRange, config: lastActivityDateRangeFilterConfig },
  [Filter.LastMessageDateRange]: { filter: Filter.LastMessageDateRange, config: lastMessageDateRangeFilterConfig },
  [Filter.Group]: { filter: Filter.Group, config: groupFilterConfigMultiple },
  [Filter.Tag]: { filter: Filter.Tag, config: tagFilterConfigMultiple },
  [Filter.Rating]: { filter: Filter.Rating, config: ratingFilterConfig },
  [Filter.TicketStatus]: { filter: Filter.TicketStatus, config: ticketStatusFilterConfig },
  [Filter.DetailedSource]: { filter: Filter.DetailedSource, config: ticketSourceFilterConfig },
  [Filter.RatingDateRange]: { filter: Filter.RatingDateRange, config: ratingDateRangeFilterConfig, readOnly: true },
  [Filter.ResolutionDateRange]: {
    filter: Filter.ResolutionDateRange,
    config: resolutionDateRangeFilterConfig,
    readOnly: true,
  },
  [Filter.ResponseDateRange]: {
    filter: Filter.ResponseDateRange,
    config: responseDateRangeFilterConfig,
    readOnly: true,
  },
  [Filter.ResponseAuthor]: { filter: Filter.ResponseAuthor, config: responseAuthorFilterConfig, readOnly: true },
  [Filter.Priority]: { filter: Filter.Priority, config: priorityFilterConfig },
  [Filter.Followers]: { filter: Filter.Followers, config: followersFilterConfig },
};

const commonFilters = [
  ticketFilters[Filter.CreationDateRange],
  ticketFilters[Filter.Language],
  ticketFilters[Filter.LastActivityDateRange],
  ticketFilters[Filter.LastMessageDateRange],
  ticketFilters[Filter.Tag],
  ticketFilters[Filter.Rating],
  ticketFilters[Filter.Priority],
  ticketFilters[Filter.Followers],
  ticketFilters[Filter.DetailedSource],
  ticketFilters[Filter.Attachments],
];

const readOnlyFilters = Object.keys(ticketFilters)
  .filter(filterKey => ticketFilters[filterKey].readOnly)
  .map(filterKey => ticketFilters[filterKey]);

// prettier-ignore
export const availableFiltersRouteMap: { [key in TicketsRoutes]: IAvailableFilter[] } = mapValues(
  {
    [TicketsRoutes.MyOpen]: [...readOnlyFilters, ...commonFilters],
    [TicketsRoutes.Unassigned]: [...readOnlyFilters, ...commonFilters],
    [TicketsRoutes.Open]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment]],
    [TicketsRoutes.Pending]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment]],
    [TicketsRoutes.OnHold]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment]],
    [TicketsRoutes.Solved]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment]],
    [TicketsRoutes.Closed]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment]],
    [TicketsRoutes.Search]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.TicketStatus], ticketFilters[Filter.AgentAssignment]],
    [TicketsRoutes.Spam]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment], ticketFilters[Filter.TicketStatus]],
    [TicketsRoutes.All]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment], ticketFilters[Filter.TicketStatus]],
    [TicketsRoutes.Archive]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment], ticketFilters[Filter.TicketStatus]],
    [TicketsRoutes.Trash]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment], ticketFilters[Filter.TicketStatus]],
    [TicketsRoutes.Undelivered]: [...readOnlyFilters, ...commonFilters, ticketFilters[Filter.Group], ticketFilters[Filter.Agent], ticketFilters[Filter.AgentAssignment], ticketFilters[Filter.TicketStatus]],
  },
  filters => sortBy(filters, filter => filter.config.title)
);
